import { format } from 'date-fns';
import { Fragment, useState } from 'react';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    LaneFilterChips,
    OUTREACH_LANE_OPTIONS,
    type OutreachLaneKey,
} from '@apps/web/components/admin/outreach_observability/lane_filter';
import { LaneOrgTrendChart } from '@apps/web/components/admin/outreach_observability/lane_org_trend_chart';
import { OutreachLifecyclePipeline } from '@apps/web/components/admin/outreach_observability/outreach_lifecycle_pipeline';
import { Input } from '@apps/web/components/ui/input';
import { cn } from '@apps/web/lib/utils';
import {
    type AutoOutreachCreatorRow,
    type AutoOutreachLaneDay,
    type AutoOutreachLaneStats,
    type AutoOutreachOrgRow,
    type AutoOutreachPipelineTag,
    type AutoOutreachSection,
    type AutoOutreachTriggerCount,
} from '@libraries/typescript/api_client';

const TRIGGER_LABELS: Record<string, string> = {
    caregiver_declined: 'Caregiver declined',
    caregiver_unassigned: 'Caregiver unassigned',
    caregiver_cancelled: 'Caregiver cancelled',
    open_shift: 'Open shift',
    agent_automation: 'Agent automation',
    unknown: 'Unknown',
};

type LaneTrendPoint = {
    readonly label: string;
    readonly outreachCount: number;
    readonly shiftsFilled: number;
    readonly shiftsCancelled: number;
    readonly shiftsUnfilled: number;
    readonly yesResponses: number;
};

type LaneTrendTooltipEntry = {
    readonly dataKey?: string | number;
    readonly name?: string;
    readonly value?: number;
    readonly color?: string;
};

function LaneTrendTooltip({
    active,
    payload,
    label,
}: {
    readonly active?: boolean;
    readonly payload?: Array<LaneTrendTooltipEntry>;
    readonly label?: string;
}) {
    if (!active || !payload || payload.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((entry) => (
                <div
                    key={entry.dataKey}
                    className="flex items-center justify-between gap-4"
                >
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span className="tabular-nums">{entry.value ?? 0}</span>
                </div>
            ))}
        </div>
    );
}

function LaneDailyTrendChart({
    label,
    days,
}: {
    readonly label: string;
    readonly days: Array<AutoOutreachLaneDay>;
}) {
    const data: Array<LaneTrendPoint> = days.map((day) => ({
        label: format(new Date(`${day.day}T00:00:00`), 'M/d'),
        outreachCount: day.outreach_count,
        shiftsFilled: day.shifts_filled,
        shiftsCancelled: day.shifts_cancelled,
        shiftsUnfilled: day.shifts_unfilled,
        yesResponses: day.yes_count,
    }));
    return (
        <div className="bg-background min-w-0 rounded-lg border p-2">
            <div className="text-muted-foreground mb-1 text-xs font-medium">
                {label}
            </div>
            <div className="h-40 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={data}
                        margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
                    >
                        <CartesianGrid
                            stroke="var(--border)"
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <RechartsTooltip content={<LaneTrendTooltip />} />
                        <Bar
                            dataKey="outreachCount"
                            name="Outreaches created"
                            fill="var(--chart-1)"
                            maxBarSize={32}
                            isAnimationActive={false}
                        />
                        <Line
                            dataKey="shiftsFilled"
                            name="Shifts filled"
                            stroke="var(--chart-2)"
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            isAnimationActive={false}
                        />
                        <Line
                            dataKey="shiftsCancelled"
                            name="Shifts cancelled"
                            stroke="var(--chart-3)"
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            isAnimationActive={false}
                        />
                        <Line
                            dataKey="shiftsUnfilled"
                            name="Shifts unfilled"
                            stroke="var(--chart-4)"
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            isAnimationActive={false}
                        />
                        <Line
                            dataKey="yesResponses"
                            name="Yes responses"
                            stroke="var(--chart-5)"
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            isAnimationActive={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

type OrgSortKey =
    | 'created_count'
    | 'total_outreach_count'
    | 'shift_fill_rate'
    | 'total_cost_usd';

function orgSortValue(row: AutoOutreachOrgRow, key: OrgSortKey): number {
    switch (key) {
        case 'created_count':
            return row.created_count;
        case 'total_outreach_count':
            return row.total_outreach_count;
        case 'shift_fill_rate':
            return row.shift_totals.covered > 0
                ? row.shift_totals.filled / row.shift_totals.covered
                : 0;
        case 'total_cost_usd':
            // Nullish fallback despite the non-null schema type: during a
            // deploy an older API can still serve sections without cost.
            return row.total_cost_usd ?? 0;
        default:
            return 0;
    }
}

type CreatorSortKey = 'started_count' | 'shift_fill_rate' | 'total_cost_usd';

function creatorSortValue(
    row: AutoOutreachCreatorRow,
    key: CreatorSortKey,
): number {
    switch (key) {
        case 'started_count':
            return row.started_count;
        case 'shift_fill_rate':
            return row.shift_totals.covered > 0
                ? row.shift_totals.filled / row.shift_totals.covered
                : 0;
        case 'total_cost_usd':
            return row.total_cost_usd ?? 0;
        default:
            return 0;
    }
}

function starterLabel(row: AutoOutreachCreatorRow): string {
    if (row.is_phoebe) {
        return 'Phoebe';
    }
    if (row.user_email) {
        return row.user_email;
    }
    return 'Unattributed';
}

function pct(count: number, denominator: number): string {
    if (denominator <= 0) {
        return '0%';
    }
    return `${((count / denominator) * 100).toFixed(0)}%`;
}

function SortHeader<K extends string>({
    label,
    sortKey,
    activeKey,
    onSort,
}: {
    readonly label: string;
    readonly sortKey: K;
    readonly activeKey: K;
    readonly onSort: (key: K) => void;
}) {
    const isActive = activeKey === sortKey;
    return (
        <th className="px-3 py-2 text-right font-medium">
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={cn(
                    'hover:text-foreground inline-flex items-center gap-0.5',
                    isActive && 'text-foreground',
                )}
            >
                {label}
                {isActive ? <span>▼</span> : null}
            </button>
        </th>
    );
}

function RateCell({
    count,
    denominator,
}: {
    readonly count: number;
    readonly denominator: number;
}) {
    return (
        <span className="tabular-nums">
            {count}
            <span className="text-muted-foreground">
                {' '}
                ({pct(count, denominator)})
            </span>
        </span>
    );
}

function TriggerChips({
    byTrigger,
    size = 'md',
}: {
    readonly byTrigger: Array<AutoOutreachTriggerCount>;
    readonly size?: 'md' | 'sm';
}) {
    return (
        <div className="flex flex-wrap gap-1">
            {byTrigger.map((entry) => (
                <span
                    key={entry.trigger}
                    className={cn(
                        'bg-muted/50 rounded-md border tabular-nums',
                        size === 'md' && 'px-2 py-1 text-xs',
                        size === 'sm' && 'px-1.5 py-0.5 text-[11px]',
                    )}
                >
                    {TRIGGER_LABELS[entry.trigger] ?? entry.trigger}:{' '}
                    {entry.count}
                </span>
            ))}
        </div>
    );
}

function ActionChips({
    actions,
}: {
    readonly actions: Array<AutoOutreachPipelineTag>;
}) {
    return (
        <div className="flex flex-wrap gap-1">
            {actions.map((tag) => (
                <span
                    key={tag.key}
                    className="bg-muted/50 rounded-md border px-1.5 py-0.5 text-[11px] tabular-nums"
                >
                    {tag.label}: {tag.count}
                </span>
            ))}
        </div>
    );
}

function LaneMetric({
    label,
    count,
    denominator,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly count: number;
    readonly denominator: number;
    readonly tone?: 'positive' | 'negative' | 'neutral';
}) {
    return (
        <div
            className={cn(
                'bg-background flex min-w-0 flex-col gap-0.5 rounded-md border p-2',
                tone === 'positive' &&
                    'border-emerald-300 dark:border-emerald-800',
                tone === 'negative' && 'border-red-300 dark:border-red-900',
            )}
        >
            <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {label}
            </span>
            <RateCell count={count} denominator={denominator} />
        </div>
    );
}

function LaneCard({
    label,
    lane,
    totalOutreachCount,
}: {
    readonly label: string;
    readonly lane: AutoOutreachLaneStats;
    readonly totalOutreachCount: number;
}) {
    const responsesReceived =
        lane.responses.yes_count +
        lane.responses.no_count +
        lane.responses.other_count;
    return (
        <div className="bg-background flex min-w-0 flex-col gap-2 rounded-lg border p-3">
            <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {label}
            </span>
            <span className="text-2xl font-semibold tabular-nums">
                {lane.outreach_count}
                <span className="text-muted-foreground text-sm font-normal">
                    {' '}
                    outreaches
                </span>
            </span>
            <span className="text-muted-foreground text-xs tabular-nums">
                {pct(lane.outreach_count, totalOutreachCount)} of all outreaches
            </span>
            <div className="bg-muted/30 grid grid-cols-2 gap-2 rounded-md p-2 text-xs">
                <span className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">Auto-started</span>
                    <RateCell
                        count={lane.auto_started_count}
                        denominator={lane.outreach_count}
                    />
                </span>
                <span className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">
                        Auto-monitored
                    </span>
                    <RateCell
                        count={lane.auto_monitored_count}
                        denominator={lane.outreach_count}
                    />
                </span>
            </div>
            <div>
                <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                    Caregiver responses
                </div>
                <div className="text-muted-foreground mb-1 text-xs tabular-nums">
                    {responsesReceived} responses received ·{' '}
                    {lane.responses.contacted_caregiver_count} caregivers
                    contacted
                </div>
                <div className="grid grid-cols-3 gap-1">
                    <LaneMetric
                        label="Yes"
                        count={lane.responses.yes_count}
                        denominator={responsesReceived}
                        tone="positive"
                    />
                    <LaneMetric
                        label="No"
                        count={lane.responses.no_count}
                        denominator={responsesReceived}
                        tone="negative"
                    />
                    <LaneMetric
                        label="Other"
                        count={lane.responses.other_count}
                        denominator={responsesReceived}
                    />
                </div>
            </div>
        </div>
    );
}

export function AutoOutreachPerformance({
    section,
}: {
    readonly section: AutoOutreachSection | null;
}) {
    const [orgSortKey, setOrgSortKey] = useState<OrgSortKey>('created_count');
    const [orgFilter, setOrgFilter] = useState('');
    // Shared lane selection: filters both the lifecycle pipelines and the
    // By-organization table below them.
    const [selectedLanes, setSelectedLanes] = useState<Set<OutreachLaneKey>>(
        new Set(OUTREACH_LANE_OPTIONS.map((option) => option.key)),
    );
    const [creatorSortKey, setCreatorSortKey] =
        useState<CreatorSortKey>('started_count');
    if (section === null) {
        return (
            <div className="text-muted-foreground flex h-40 items-center justify-center rounded-md border text-sm">
                Loading…
            </div>
        );
    }
    const { lanes } = section;
    const orgFilterNeedle = orgFilter.trim().toLowerCase();
    const sortedOrgs = section.by_organization
        .filter((row) =>
            (row.organization_name ?? row.organization_id)
                .toLowerCase()
                .includes(orgFilterNeedle),
        )
        .sort(
            (a, b) => orgSortValue(b, orgSortKey) - orgSortValue(a, orgSortKey),
        );
    const totalOutreachCount =
        lanes.auto_drafted.outreach_count +
        lanes.agent_outreach.outreach_count +
        lanes.manual_outreach.outreach_count;
    const visibleOrgLanes = OUTREACH_LANE_OPTIONS.filter((option) =>
        selectedLanes.has(option.key),
    );
    // Drop empty lane rows (and orgs with no remaining rows) so the table
    // only shows lanes an org actually uses.
    const rowLanesFor = (row: AutoOutreachOrgRow) =>
        visibleOrgLanes.filter(
            (lane) => row.lanes[lane.key].outreach_count > 0,
        );
    const displayedOrgs = sortedOrgs.filter(
        (row) => rowLanesFor(row).length > 0,
    );
    // Phoebe stays pinned first regardless of the sort column.
    const sortedCreators = [...section.by_creator].sort((a, b) => {
        if (a.is_phoebe !== b.is_phoebe) {
            return a.is_phoebe ? -1 : 1;
        }
        return (
            creatorSortValue(b, creatorSortKey) -
            creatorSortValue(a, creatorSortKey)
        );
    });
    return (
        <div className="flex w-full max-w-full min-w-0 flex-col gap-4">
            <div className="bg-muted/30 flex w-full max-w-full min-w-0 flex-col gap-2 overflow-hidden rounded-lg border p-3">
                <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    Outreach lanes
                </div>
                <div className="w-full max-w-full min-w-0 overflow-x-auto pb-2">
                    <div className="grid w-full min-w-[66rem] grid-cols-3 gap-2">
                        <LaneCard
                            label="Auto-drafted"
                            lane={lanes.auto_drafted}
                            totalOutreachCount={totalOutreachCount}
                        />
                        <LaneCard
                            label="Agent outreach"
                            lane={lanes.agent_outreach}
                            totalOutreachCount={totalOutreachCount}
                        />
                        <LaneCard
                            label="Manual outreach"
                            lane={lanes.manual_outreach}
                            totalOutreachCount={totalOutreachCount}
                        />
                    </div>
                </div>
                <div className="text-muted-foreground mt-1 text-xs font-medium tracking-wide uppercase">
                    Daily trend
                </div>
                <div className="w-full max-w-full min-w-0 overflow-x-auto pb-2">
                    <div className="grid w-full min-w-[66rem] grid-cols-3 gap-2">
                        <LaneDailyTrendChart
                            label="Auto-drafted"
                            days={lanes.auto_drafted.days}
                        />
                        <LaneDailyTrendChart
                            label="Agent outreach"
                            days={lanes.agent_outreach.days}
                        />
                        <LaneDailyTrendChart
                            label="Manual outreach"
                            days={lanes.manual_outreach.days}
                        />
                    </div>
                </div>
                <LaneOrgTrendChart days={section.org_days} />
            </div>
            <div>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                        Lifecycle
                    </div>
                    <LaneFilterChips
                        selected={selectedLanes}
                        onChange={setSelectedLanes}
                    />
                </div>
                <OutreachLifecyclePipeline
                    // Nullish fallback despite the non-null schema type:
                    // during a deploy an older API can still serve cached
                    // sections without the pipeline.
                    pipeline={section.pipeline ?? null}
                    selectedLanes={selectedLanes}
                />
            </div>
            {section.by_organization.length > 0 ? (
                <div>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                        <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                            By organization
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-muted-foreground text-xs tabular-nums">
                                {displayedOrgs.length} of{' '}
                                {section.by_organization.length} orgs
                            </span>
                            <Input
                                value={orgFilter}
                                onChange={(event) =>
                                    setOrgFilter(event.target.value)
                                }
                                placeholder="Filter organizations…"
                                className="h-7 w-56 text-xs"
                            />
                        </div>
                    </div>
                    <div className="max-h-[36rem] overflow-auto rounded-md border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                                <tr className="text-muted-foreground text-left">
                                    <th className="px-3 py-2 font-medium">
                                        Organization
                                    </th>
                                    <th className="px-3 py-2 font-medium">
                                        Lane
                                    </th>
                                    <SortHeader
                                        label="Outreaches / org total"
                                        sortKey="created_count"
                                        activeKey={orgSortKey}
                                        onSort={setOrgSortKey}
                                    />
                                    <th className="px-3 py-2 text-right font-medium">
                                        Auto-started
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Auto-monitored
                                    </th>
                                    <SortHeader
                                        label="Shifts filled"
                                        sortKey="shift_fill_rate"
                                        activeKey={orgSortKey}
                                        onSort={setOrgSortKey}
                                    />
                                    <th className="px-3 py-2 text-right font-medium">
                                        Cancelled
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Unfilled
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Pending
                                    </th>
                                    <SortHeader
                                        label="Cost"
                                        sortKey="total_cost_usd"
                                        activeKey={orgSortKey}
                                        onSort={setOrgSortKey}
                                    />
                                    <th className="px-3 py-2 font-medium">
                                        Why Phoebe drafted
                                    </th>
                                    <th className="px-3 py-2 font-medium">
                                        Actions observed
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedOrgs.length === 0 ? (
                                    <tr className="border-t">
                                        <td
                                            colSpan={12}
                                            className="text-muted-foreground px-3 py-4 text-center"
                                        >
                                            No organizations match the filter.
                                        </td>
                                    </tr>
                                ) : null}
                                {displayedOrgs.map((row) => (
                                    <Fragment key={row.organization_id}>
                                        {rowLanesFor(row).map(
                                            (laneDefinition, laneIndex) => {
                                                const lane =
                                                    row.lanes[
                                                        laneDefinition.key
                                                    ];
                                                // Nullish fallback despite
                                                // the non-null schema type:
                                                // a cached pre-deploy
                                                // section can omit actions.
                                                const laneActions =
                                                    lane.actions ?? [];
                                                return (
                                                    <tr
                                                        key={laneDefinition.key}
                                                        className={cn(
                                                            laneIndex === 0
                                                                ? 'border-t-2'
                                                                : 'border-t',
                                                        )}
                                                    >
                                                        {laneIndex === 0 ? (
                                                            <td
                                                                rowSpan={
                                                                    rowLanesFor(
                                                                        row,
                                                                    ).length
                                                                }
                                                                className="bg-background px-3 py-2 align-top font-medium"
                                                            >
                                                                {row.organization_name ??
                                                                    row.organization_id}
                                                            </td>
                                                        ) : null}
                                                        <td className="px-3 py-2 font-medium whitespace-nowrap">
                                                            {
                                                                laneDefinition.label
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane.outreach_count
                                                            }
                                                            <span className="text-muted-foreground">
                                                                {' '}
                                                                /{' '}
                                                                {
                                                                    row.total_outreach_count
                                                                }{' '}
                                                                (
                                                                {pct(
                                                                    lane.outreach_count,
                                                                    row.total_outreach_count,
                                                                )}
                                                                )
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane.auto_started_count
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane.auto_monitored_count
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right">
                                                            <RateCell
                                                                count={
                                                                    lane
                                                                        .shift_totals
                                                                        .filled
                                                                }
                                                                denominator={
                                                                    lane
                                                                        .shift_totals
                                                                        .covered
                                                                }
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane
                                                                    .shift_totals
                                                                    .cancelled
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane
                                                                    .shift_totals
                                                                    .unfilled
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {
                                                                lane
                                                                    .shift_totals
                                                                    .pending
                                                            }
                                                        </td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            $
                                                            {(
                                                                lane.total_cost_usd ??
                                                                0
                                                            ).toFixed(2)}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {lane.by_trigger
                                                                .length > 0 ? (
                                                                <TriggerChips
                                                                    byTrigger={
                                                                        lane.by_trigger
                                                                    }
                                                                    size="sm"
                                                                />
                                                            ) : (
                                                                <span className="text-muted-foreground">
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            {laneActions.length >
                                                            0 ? (
                                                                <ActionChips
                                                                    actions={
                                                                        laneActions
                                                                    }
                                                                />
                                                            ) : (
                                                                <span className="text-muted-foreground">
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            },
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
            {sortedCreators.length > 0 ? (
                <div>
                    <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                        By user
                    </div>
                    <div className="max-h-[36rem] overflow-auto rounded-md border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50 sticky top-0">
                                <tr className="text-muted-foreground text-left">
                                    <th className="px-3 py-2 font-medium">
                                        User
                                    </th>
                                    <SortHeader
                                        label="Outreaches / total"
                                        sortKey="started_count"
                                        activeKey={creatorSortKey}
                                        onSort={setCreatorSortKey}
                                    />
                                    <th className="px-3 py-2 text-right font-medium">
                                        Auto-started
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Auto-monitored
                                    </th>
                                    <SortHeader
                                        label="Shifts filled"
                                        sortKey="shift_fill_rate"
                                        activeKey={creatorSortKey}
                                        onSort={setCreatorSortKey}
                                    />
                                    <th className="px-3 py-2 text-right font-medium">
                                        Cancelled
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Unfilled
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                        Pending
                                    </th>
                                    <SortHeader
                                        label="Cost"
                                        sortKey="total_cost_usd"
                                        activeKey={creatorSortKey}
                                        onSort={setCreatorSortKey}
                                    />
                                    <th className="px-3 py-2 font-medium">
                                        Why Phoebe drafted
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCreators.map((row) => (
                                    <tr
                                        key={row.user_id ?? 'unattributed'}
                                        className="border-t"
                                    >
                                        <td className="px-3 py-2 font-medium">
                                            {starterLabel(row)}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.started_count}
                                            <span className="text-muted-foreground">
                                                {' '}
                                                / {totalOutreachCount} (
                                                {pct(
                                                    row.started_count,
                                                    totalOutreachCount,
                                                )}
                                                )
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.is_phoebe
                                                ? row.started_count
                                                : 0}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.auto_monitored_count}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <RateCell
                                                count={row.shift_totals.filled}
                                                denominator={
                                                    row.shift_totals.covered
                                                }
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.shift_totals.cancelled}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.shift_totals.unfilled}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            {row.shift_totals.pending}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">
                                            $
                                            {(row.total_cost_usd ?? 0).toFixed(
                                                2,
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            {row.by_trigger.length > 0 ? (
                                                <TriggerChips
                                                    byTrigger={row.by_trigger}
                                                    size="sm"
                                                />
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    —
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
